import { type ReactElement, type ReactNode, useCallback } from "react";
import {
  type FallbackProps,
  ErrorBoundary as ReactErrorBoundary,
  type ErrorBoundaryProps as ReactErrorBoundaryProps,
} from "react-error-boundary";
import { RouteError } from "./route-error.js";

export interface ErrorBoundaryProps {
  /**
   * Custom fallback. Receives the error and a `reset` callback that
   * tells `react-error-boundary` to clear its error state and re-mount
   * the children. Defaults to `<RouteError>`.
   */
  readonly fallback?: (error: Error, reset: () => void) => ReactNode;
  /**
   * Reported on every caught render error. Use this to forward the
   * error to your logger or to dispatch a `cakewalk:error-boundary`
   * DOM event (which `@cakewalk/frontend-api` listens for).
   */
  readonly onError?: (error: Error, info: { componentStack: string }) => void;
  /** When any of these change, the boundary resets. */
  readonly resetKeys?: ReadonlyArray<unknown>;
  readonly children: ReactNode;
}

/**
 * Wrapper around `react-error-boundary`. Every route in every app sits
 * inside one. Per `cross-cutting/frontend-states.mdc`:
 *
 * - Catches RENDER errors (network errors go through TanStack Query and
 *   `<DataState>`).
 * - Renders a `<RouteError>` panel by default.
 * - Reports the error via `onError` so the logger / analytics can pick
 *   it up.
 */
export function ErrorBoundary({ fallback, onError, resetKeys, children }: ErrorBoundaryProps) {
  const handleError = useCallback<NonNullable<ReactErrorBoundaryProps["onError"]>>(
    (error, info) => {
      const componentStack = info?.componentStack ?? "";
      onError?.(error, { componentStack });
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("cakewalk:error-boundary", {
            detail: { message: error.message, name: error.name, componentStack },
          }),
        );
      }
    },
    [onError],
  );

  return (
    <ReactErrorBoundary
      onError={handleError}
      resetKeys={resetKeys ? [...resetKeys] : undefined}
      fallbackRender={({ error, resetErrorBoundary }: FallbackProps): ReactElement => (
        <>
          {fallback ? (
            fallback(error as Error, resetErrorBoundary)
          ) : (
            <RouteError error={error as Error} onReset={resetErrorBoundary} />
          )}
        </>
      )}
    >
      {children}
    </ReactErrorBoundary>
  );
}
